/**
 * Field-level instrumentation for checkout, on both the guest and signed-in
 * paths.
 *
 * WHY THIS EXISTS. The funnel could say a shopper reached checkout and did not
 * buy, and nothing between those two facts. On production in the week to
 * 2026-09-21 that was 112 arrivals a day and roughly one typed email: the
 * shape of a page people bounce off, with no record of which field they were
 * looking at when they left. A step they never completed and a step they never
 * attempted read identically.
 *
 * NO VALUES, EVER. `trackFieldChange` in utils/track-funnel.ts sends the field's
 * value, which is right for a listing title and wrong for everything on this
 * page -- email, name, street address, postcode are the whole of a buyer's
 * identity, and a funnel table is not where that belongs. Everything here
 * reports SHAPE instead: filled or empty, valid or not, how long they sat in
 * the field. `length` is the one number that touches the value, and it is a
 * count, not the content.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not track keystrokes. A debounced
 * change event per field is already available and adds nothing a blur does not
 * say more cheaply, and `MAX_EVENTS_PER_SESSION` is a real ceiling -- spending
 * it on typing would push the events that matter, the errors, out of the
 * session.
 */
import { trackFunnelEvent } from './track-funnel';
import { updateAbandonmentMetadata } from './track-abandonment';

/**
 * The fields worth naming.
 *
 * A closed set rather than a free string: these names become the columns of
 * every funnel breakdown built on them, and one caller passing `guestEmail`
 * where the rest pass `guest_email` splits a step in two on the chart.
 */
export const CHECKOUT_FIELDS = [
  'guest_email',
  'guest_name',
  'guest_address',
  'guest_address_line2',
  'guest_city',
  'guest_state',
  'guest_postal_code',
  'coupon',
  'shipping_option',
  'saved_address',
  'payment_card',
] as const;

export type CheckoutField = (typeof CHECKOUT_FIELDS)[number];

/** Which checkout the shopper is in. Guest and signed-in fail differently. */
export interface CheckoutFieldContext {
  loggedIn: boolean;
  listingId?: number | string | null;
}

function base(field: CheckoutField, ctx: CheckoutFieldContext) {
  return {
    field,
    is_guest: ctx.loggedIn ? 0 : 1,
    listing_id: ctx.listingId ? Number(ctx.listingId) : undefined,
  };
}

// Every caller is a focus, blur or submit handler or an effect in the checkout
// components, so these only ever run in the browser.

/** When a field was first entered, so a dwell can be measured against it. */
const focusedAt = new Map<CheckoutField, number>();

/** Inputs already counting their length for the abandonment, so a refocus adds no second listener. */
const counted = new WeakSet<EventTarget>();

/**
 * Tell a departure which field they were in and how much was in it.
 *
 * A blur is sent only for a changed field, and leaving the page mid-field
 * sends no blur at all, so "tapped the name and left" and "typed half a name
 * and left" looked the same (stories, 2026-11-02). The length rides on the
 * abandonment row, which is sent on the way out. A count, never the content.
 */
function watchForDeparture(field: CheckoutField): void {
  const el = typeof document === 'undefined' ? null : document.activeElement;
  const length = () => (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? el.value.length : undefined);
  updateAbandonmentMetadata('checkout', { last_field: field, last_field_length: length() });
  if (!el || counted.has(el)) return;
  counted.add(el);
  el.addEventListener('input', () => updateAbandonmentMetadata('checkout', { last_field: field, last_field_length: length() }));
}

/**
 * The shopper put the cursor in a field.
 *
 * Deduplicated per field by `dedupKey`: a shopper who clicks in and out of the
 * address four times is one person struggling with one field, and four
 * identical rows would read as four.
 */
export function trackCheckoutFieldFocus(field: CheckoutField, ctx: CheckoutFieldContext): void {
  if (!focusedAt.has(field)) focusedAt.set(field, Date.now());
  watchForDeparture(field);
  trackFunnelEvent('checkout', 'field_focus', base(field, ctx), {
    component: 'checkout-fields',
    dedupKey: `focus:${field}`,
  });
}

/**
 * The shopper left a field, and what state they left it in.
 *
 * This is the event that separates "never tried" from "tried and gave up".
 * `dwell_ms` is measured from the first focus rather than the last, because a
 * shopper who leaves and comes back is still spending that time on this field.
 */
export function trackCheckoutFieldBlur(
  field: CheckoutField,
  ctx: CheckoutFieldContext,
  state: { filled: boolean; valid?: boolean; length?: number },
): void {
  const since = focusedAt.get(field);
  trackFunnelEvent(
    'checkout',
    'field_blur',
    {
      ...base(field, ctx),
      filled: state.filled ? 1 : 0,
      ...(state.valid === undefined ? {} : { valid: state.valid ? 1 : 0 }),
      // A count, never the content. Distinguishes a typo from a blank.
      ...(state.length === undefined ? {} : { length: state.length }),
      ...(since ? { dwell_ms: Date.now() - since } : {}),
    },
    { component: 'checkout-fields', dedupKey: `blur:${field}:${state.filled ? 1 : 0}` },
  );
}

/**
 * A message the shopper actually saw, under the field that produced it.
 *
 * Sent immediately: the most valuable error on this page is the one on the last
 * thing they did before closing the tab, and a queued event does not survive
 * that. `reason` is the rule that fired -- `empty`, `invalid`, `declined` --
 * never the text, so a copy change does not fragment the history.
 */
export function trackCheckoutFieldError(
  field: CheckoutField,
  ctx: CheckoutFieldContext,
  reason: string,
): void {
  trackFunnelEvent(
    'checkout',
    'field_error',
    { ...base(field, ctx), reason },
    { component: 'checkout-fields', immediate: true, dedupKey: `err:${field}:${reason}` },
  );
}

/**
 * The shopper pressed something that refused to advance.
 *
 * Pressing Continue or Pay and staying where you were is the single loudest
 * signal of friction on this page, and until now it left no trace at all: the
 * click was not tracked, the refusal was a local `setState`, and the funnel saw
 * a session that simply stopped. `blocked_by` names the first thing that
 * refused, so the reason is countable.
 */
export function trackCheckoutBlocked(
  ctx: CheckoutFieldContext,
  // `pay` since the one-step page (2026-10-25): there is no Continue button.
  action: 'pay',
  blockedBy: string,
): void {
  trackFunnelEvent(
    'checkout',
    'blocked',
    {
      action,
      blocked_by: blockedBy,
      is_guest: ctx.loggedIn ? 0 : 1,
      listing_id: ctx.listingId ? Number(ctx.listingId) : undefined,
    },
    { component: 'checkout-fields', immediate: true, dedupKey: `blocked:${action}:${blockedBy}` },
  );
}

/** Test-only: forget the dwell clocks between cases. */
export function resetCheckoutFieldTrackingForTests(): void {
  focusedAt.clear();
}
